import * as React from 'react';
import { Icon } from '@antimatterhq/ui/components/icon';
import { dbui_v1_WarehouseItem } from '@antimatterhq/asl-api-ts-v1';
import toast from 'react-hot-toast';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@antimatterhq/ui/components/select';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { useGetWarehouseLocations } from '@/hooks/dbui/useGetWarehouseLocations';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';
import { useCreateStartWarehouseById } from '@/hooks/dbui/useCreateStartWarehouseById';
import { cn } from '@/lib/utils';

interface CustomSelectItemProps {
  value: string;
  state: dbui_v1_WarehouseItem['state'];
}

const CustomSelectItem: React.FC<CustomSelectItemProps> = ({
  value,
  state,
}) => {
  const getIcon = () => {
    switch (state) {
      case 'RUNNING':
        return (
          <Icon icon="mdi:circle" className="mr-2 h-4 w-4 text-green-500" />
        );
      case 'STARTING':
        return (
          <Icon
            icon="lucide:loader-circle"
            className="mr-2 h-4 w-4 animate-spin text-gray-400"
          />
        );
      case 'STOPPING':
      case 'STOPPED':
      case 'DELETING':
      case 'DELETED':
        return (
          <Icon icon="lucide:circle-x" className="mr-2 h-4 w-4 text-red-500" />
        );
      default:
        return (
          <Icon
            icon="lucide:loader-circle"
            className="mr-2 h-4 w-4 animate-spin"
          />
        );
    }
  };

  return (
    <div className="flex items-center">
      {getIcon()}
      <span>{value}</span>
    </div>
  );
};

interface WarehouseLocationsDropdownProps {
  className?: string;
}

export default function WarehouseLocationsDropdown({
  className,
}: WarehouseLocationsDropdownProps) {
  const { data, refetch } = useGetWarehouseLocations();
  const { selectedWarehouse, setSelectedWarehouse } = useWarehouseLocation();

  const { mutateAsync: startWarehouseById } = useCreateStartWarehouseById();

  const availableWarehouses = data?.warehouses.filter(
    (warehouse) =>
      warehouse.state !== 'DELETING' && warehouse.state !== 'DELETED',
  );

  const handleValueChange = async (warehouseId: string) => {
    const selectedWarehouseObj = data?.warehouses?.find(
      (warehouse) => warehouse.id === warehouseId,
    );

    // Start warehouse if it's in STOPPED state
    if (selectedWarehouseObj?.state === 'STOPPED') {
      try {
        await startWarehouseById({ id: warehouseId });
        // Only set the selected warehouse if the mutation is successful
        setSelectedWarehouse(selectedWarehouseObj);

        refetch();
      } catch {
        // @rockia: TODO: Need to find a way to handle error globally
        toast.error('Failed to start this warehouse location');
      }
    } else {
      setSelectedWarehouse(selectedWarehouseObj);
    }
  };

  return (
    <div className={cn('flex flex-col items-start space-y-4', className)}>
      <Select value={selectedWarehouse?.id} onValueChange={handleValueChange}>
        <SelectTrigger className="h-7 w-[280px]">
          <SelectValue placeholder="Select a server">
            {selectedWarehouse && (
              <CustomSelectItem
                value={selectedWarehouse.name}
                state={selectedWarehouse.state}
              />
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {(availableWarehouses || []).length > 0 ? (
              <SelectLabel>SQL warehouse</SelectLabel>
            ) : (
              <SelectLabel>No available warehouses</SelectLabel>
            )}
            {availableWarehouses?.map((warehouse) => (
              <SelectItem value={warehouse.id} key={warehouse.id}>
                <Tooltip
                  content={`Status: ${warehouse.state}`}
                  variant="info"
                  side="left"
                  delayDuration={100}
                >
                  <CustomSelectItem
                    value={warehouse.name}
                    state={warehouse.state}
                  />
                </Tooltip>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}
